import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useConfirm } from 'material-ui-confirm';
import { LoadingButton } from '@mui/lab';
import {
  Autocomplete,
  Button,
  ButtonBase,
  FormControl,
  Grid,
  Stack,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';

import { currencyOptions } from '../../../../../../../constants';
import {
  TUpdateCourseAsAdmin,
  getCoursesState,
  updateCourseAsAdmin,
} from '../../../../../../../store/V2/CoursesSlice';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../../../../store/UserSlice';
import { getAuthState } from '../../../../../../../store/AuthSlice';
import { useAppSelector } from '../../../../../../../hooks';
import { checkUserPermission } from '../../../../../../../helper';
import {
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../../../../components/Inputs';

type TSelectOption = {
  label: string;
  value: string;
};

interface PricingTabProps {
  onClose: () => void;
}

type TState = {
  amount: string;
  selectedCurrency: TSelectOption | null;
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const initialSubscriptionDetails = [
  'Comprehensive Revision of Syllabus',
  'Certified Subject Specialist Tutors',
  'live and interactive online group learning',
  'Exam Focussed Approach',
  'Past Paper Practice',
  'Recorded Lessons',
  'Access to our Learning Platform Resources',
];

const initialState: TState = {
  selectedCurrency: null,
  amount: '',
};

const PricingTab: React.FC<PricingTabProps> = ({ onClose }) => {
  const dispatch = useDispatch();
  const confirm = useConfirm();

  // Store
  const coursesState = useAppSelector(getCoursesState);
  const authState = useAppSelector(getAuthState);

  // State
  const [state, setState] = useState(initialState);
  const [subscriptionDetails, setSubscriptionDetails] = useState(
    initialSubscriptionDetails
  );

  const [isLoading, setLoading] = useState(false);

  const courseData = coursesState.adminCourse;
  const isSubmitDisabled =
    !state.amount.trim().length ||
    Number(state.amount) < 1 ||
    !state.selectedCurrency ||
    coursesState.status === 'loading';

  useEffect(() => {
    if (courseData) {
      setState((prevState) => {
        const newState: TState = { ...prevState };

        if (courseData.currency) {
          newState.selectedCurrency = {
            label: courseData.currency,
            value: courseData.currency,
          };
        }

        if (courseData.amount) {
          newState.amount = courseData.amount.toString();
        }

        return newState;
      });

      if (courseData.pricingDetails && courseData.pricingDetails.length) {
        setSubscriptionDetails(courseData.pricingDetails);
      }
    }
  }, []);

  const handleCurrencyChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    setState((prevState) => ({
      ...prevState,
      selectedCurrency: value,
    }));
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.currentTarget;

    setState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    if (courseData && state.selectedCurrency && state.amount) {
      setLoading(true);
      const apiData: TUpdateCourseAsAdmin = {
        _id: courseData._id,
        currency: state.selectedCurrency.value,
        amount: Number(state.amount),
        pricingDetails: subscriptionDetails,
      };

      await dispatch(updateCourseAsAdmin(apiData));

      setLoading(false);
    }
  };

  const handleDeleteSubscriptionDetail = (subscriptionDetail: string) => {
    confirm({
      title: 'Are you sure?',
      description: `Do you want to delete "${subscriptionDetail}"?`,
    }).then(() => {
      setSubscriptionDetails((prevState) =>
        prevState.filter((item) => item !== subscriptionDetail)
      );
    });
  };

  const handleEditSubscriptionDetail = (event, itemIndex) => {
    setSubscriptionDetails((prevState) => {
      const _newList = [...prevState];

      _newList[itemIndex] = event.target.value;

      return _newList;
    });
  };

  const handleAddNewDetail = () => {
    setSubscriptionDetails((prevState) => [...prevState, '']);
  };

  return (
    <div style={{ padding: '50px 30px 30px 30px' }}>
      <div>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4} lg={4}>
            <FormControl fullWidth>
              <Autocomplete
                size="small"
                value={state.selectedCurrency}
                options={currencyOptions}
                onChange={handleCurrencyChange}
                popupIcon={TPAutoCompleteArrowIconComponent}
                isOptionEqualToValue={(option, value) =>
                  option.label === value.value
                }
                renderInput={(params) => (
                  <TPTextField
                    {...params}
                    {...textFieldProps}
                    size="small"
                    label="Currency"
                    placeholder="Currency"
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={4}>
            <FormControl fullWidth>
              <TPTextField
                {...textFieldProps}
                name="amount"
                type="number"
                size="small"
                label="Amount"
                placeholder="Amount"
                value={state.amount}
                onChange={handleInputChange}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6">Subscription Details</Typography>
              {checkUserPermission(authState.user, {
                adminCourse: [USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_EDIT],
              }) ? (
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleAddNewDetail}
                  disabled={isLoading}
                >
                  Add New Detail
                </Button>
              ) : null}
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Stack gap={2}>
              {subscriptionDetails.map((subscriptionDetail, index) => {
                return (
                  <Stack
                    key={`subs-detail-li-${index}`}
                    direction="row"
                    alignItems="center"
                    gap={2}
                  >
                    <TPTextField
                      size="small"
                      placeholder="New Detail"
                      value={subscriptionDetail}
                      sx={{ flexGrow: 1 }}
                      onChange={(event) =>
                        handleEditSubscriptionDetail(event, index)
                      }
                    />
                    {checkUserPermission(authState.user, {
                      adminCourse: [
                        USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_EDIT,
                      ],
                    }) ? (
                      <ButtonBase
                        onClick={() =>
                          handleDeleteSubscriptionDetail(subscriptionDetail)
                        }
                        sx={{
                          backgroundColor: '#F1F5F9',
                          padding: '5px',
                          borderRadius: '5px',
                        }}
                      >
                        <DeleteIcon color="error" />
                      </ButtonBase>
                    ) : null}
                  </Stack>
                );
              })}
            </Stack>
          </Grid>
        </Grid>
      </div>
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        mt="60px"
        spacing="10px"
      >
        <Button
          disableElevation
          variant="contained"
          color="inherit"
          onClick={onClose}
        >
          Dismiss
        </Button>
        {checkUserPermission(authState.user, {
          adminCourse: [USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_EDIT],
        }) ? (
          <LoadingButton
            disableElevation
            variant="contained"
            color="primary"
            disabled={isSubmitDisabled}
            loading={isLoading}
            onClick={handleSubmit}
          >
            Save
          </LoadingButton>
        ) : null}
      </Stack>
    </div>
  );
};

export default PricingTab;
